import { styled } from '@mui/material';
import { Box, IconButton, Paper, TextField } from '@mui/material';

// Common icon button styles with theme integration
export const StyledIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.main,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.1)',
  },
  '& svg, & svg *, & path, & circle, & rect, & line, & polygon, & polyline': {
    stroke: `${theme.palette.text.main} !important`,
    fill: `${theme.palette.text.main} !important`,
    color: `${theme.palette.text.main} !important`,
  },
}));

// Container for the entire table header
export const TableHeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end', // Always push content to the right
  padding: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  minWidth: 0,
  overflow: 'visible',
}));

// Container for the header actions (buttons) on the right side
export const TableHeaderActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(1.5),
  minWidth: 0,
  overflow: 'visible',
}));

// Container for the header title on the left side
export const TableHeaderTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontFamily: 'General Sans',
  fontWeight: 500,
  fontSize: '18px',
  lineHeight: '150%',
  color: theme.palette.text.primary,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  marginRight: 'auto', // Push this to the left and actions to the right
}));

// Expandable search container
export const SearchContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isExpanded',
})<{ isExpanded: boolean }>(({ isExpanded }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  width: isExpanded ? 200 : 40,
  height: 40,
  overflow: 'hidden',
  position: 'relative',
  transition: 'width 0.3s ease-in-out',
}));

// Styled search input with animations
export const StyledSearchInput = styled(TextField)(({ theme }) => ({
  minWidth: 0,
  width: 200,
  '& .MuiOutlinedInput-root': {
    height: 32,
    animation: 'searchInputSlideInFromRight 0.3s ease-in-out',
    overflow: 'hidden',
  },
  '& .MuiOutlinedInput-input': {
    padding: '6px 8px',
    fontFamily: 'General Sans',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0.5%',
    color: theme.palette.text.main,
  },
  '@keyframes searchInputSlideInFromRight': {
    '0%': {
      transform: 'translateX(100%)',
      opacity: 0,
    },
    '50%': {
      transform: 'translateX(50%)',
      opacity: 0.7,
    },
    '100%': {
      transform: 'translateX(0)',
      opacity: 1,
    },
  },
}));

// Close button for search input
export const SearchCloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.text.main,
  padding: theme.spacing(0.25),
  minWidth: 'auto',
  width: 18,
  height: 18,
  '& svg, & svg *, & path, & circle, & rect': {
    stroke: `${theme.palette.text.main} !important`,
    fill: `${theme.palette.text.main} !important`,
    color: `${theme.palette.text.main} !important`,
    fontSize: '0.75rem !important',
  },
}));

// Column menu dropdown
export const ColumnMenuDropdown = styled(Paper)(({ theme }) => ({
  position: 'absolute',
  top: '100%',
  right: 0,
  marginTop: theme.spacing(1),
  minWidth: 320,
  maxHeight: 300,
  overflow: 'auto',
  zIndex: 1000,
  padding: theme.spacing(1),
  animation: 'columnsMenuSlideIn 0.2s ease-in-out',
  transformOrigin: 'top right',
  '@keyframes columnsMenuSlideIn': {
    '0%': {
      opacity: 0,
      transform: 'scale(0.95) translateY(-10px)',
    },
    '100%': {
      opacity: 1,
      transform: 'scale(1) translateY(0)',
    },
  },
}));

// Column menu content container
export const ColumnMenuContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

// Hidden icon preloader container
export const IconPreloader = styled(Box)({
  display: 'none',
});
